import type { Application } from '@owl/shared';
import { useState } from 'react';
import { computeAppHealth } from '../lib/appHealth';
import { Icon } from './icons';

export function OwlLogo({ size = 28 }: { size?: number }) {
  return (
    <img
      src="/owl-white.png"
      width={size}
      height={size}
      alt="OWL"
      style={{ display: 'block', objectFit: 'contain' }}
    />
  );
}

export function Brand() {
  return (
    <div className="brand-mark">
      <OwlLogo size={26} />
      <div className="brand-text">
        <div className="brand-wordmark">OWL</div>
        <div className="brand-tag">Observe · Warn · Log</div>
      </div>
    </div>
  );
}

export function StatusDot({ status }: { status: string }) {
  return <span className={`dot ${status}`} title={status} />;
}

export function AppHealthDot({ app }: { app: Pick<Application, 'status' | 'lastLogAt'> }) {
  const health = computeAppHealth(app);
  const title =
    health === 'idle'
      ? app.status
      : health === 'ok'
        ? 'active · logs recientes'
        : health === 'warn'
          ? 'active · sin logs >10min'
          : 'active · sin logs >1h';
  return <span className={`dot health-${health}`} title={title} />;
}

export function EnvChip({ environment }: { environment: 'development' | 'production' }) {
  const isDev = environment === 'development';
  return (
    <span className={`chip-env ${isDev ? 'env-dev' : 'env-prod'}`} title={environment}>
      {isDev ? 'DEV' : 'PROD'}
    </span>
  );
}

export type MessageTypeLike = { name: string; color: string };

export function MessageTypeChip({
  mt,
  dense = false,
}: { mt: MessageTypeLike | null | undefined; dense?: boolean }) {
  if (!mt) return null;
  const bg = `${mt.color}1F`;
  const border = `${mt.color}55`;
  return (
    <span
      className="chip chip-mt"
      style={{
        background: bg,
        borderColor: border,
        color: mt.color,
        padding: dense ? '1px 6px' : '1.5px 8px',
      }}
    >
      {mt.name}
    </span>
  );
}

export function SeverityChip({ severity }: { severity: 'low' | 'medium' | 'high' | 'critical' }) {
  const labels = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
  return <span className={`severity-chip sev-${severity}`}>{labels[severity]}</span>;
}

export function TokenBadge({
  token,
  full = false,
  onCopy,
  onRegenerate,
}: {
  token: string;
  full?: boolean;
  onCopy?: (token: string) => void;
  onRegenerate?: () => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const tail = token.slice(-4);
  const display = revealed ? token : `${token.slice(0, 12)}••••••${tail}`;
  return (
    <div className="token-badge">
      <span style={{ flex: full ? 1 : 'none' }}>{display}</span>
      <button
        type="button"
        className="token-action"
        onClick={() => setRevealed((r) => !r)}
        title={revealed ? 'Ocultar' : 'Mostrar'}
      >
        <Icon.Eye size={11} />
      </button>
      <button
        type="button"
        className="token-action"
        onClick={() => onCopy?.(token)}
        title="Copiar token"
      >
        <Icon.Copy size={11} /> Copy
      </button>
      {onRegenerate && (
        <button type="button" className="token-action" onClick={onRegenerate} title="Regenerar">
          <Icon.Refresh size={11} />
        </button>
      )}
    </div>
  );
}

export function ChannelBadge({
  channel,
  status,
}: {
  channel: 'email' | 'webhook' | 'slack' | 'discord' | 'push';
  status?: 'sent' | 'failed' | 'silenced';
}) {
  const labels = {
    email: 'Email',
    webhook: 'Webhook',
    slack: 'Slack',
    discord: 'Discord',
    push: 'Push',
  };
  const Ico = channel === 'push' ? Icon.Bell : Icon.Mail;
  const cls = status ? ` ${status}` : '';
  const statusIcon =
    status === 'sent' ? '✓' : status === 'failed' ? '✗' : status === 'silenced' ? '🔇' : null;
  return (
    <span className={`chan-badge${cls}`}>
      <Ico size={11} />
      {labels[channel]}
      {statusIcon && <span style={{ marginLeft: 2 }}>{statusIcon}</span>}
    </span>
  );
}

export function copyToClipboard(text: string, onCopied?: () => void): void {
  navigator.clipboard
    .writeText(text)
    .then(() => onCopied?.())
    .catch(() => {});
}

// KPI tile used by the Overview and the per-application dashboard.
export function Kpi({
  label,
  value,
  unit,
  icon,
  delta,
  spark,
}: {
  label: string;
  value: string | number;
  unit?: string;
  icon?: (p: { size?: number; className?: string }) => JSX.Element;
  delta?: string;
  spark?: JSX.Element;
}) {
  const Ico = icon;
  return (
    <div className="kpi">
      <div className="kpi-label">
        {Ico && <Ico className="kpi-icon" />}
        <span className="kpi-label-text">{label}</span>
      </div>
      <div className="kpi-value mono">
        {value}
        {unit && <span className="kpi-value-unit">{unit}</span>}
      </div>
      {delta && <div className="kpi-delta">{delta}</div>}
      {spark && <div className="kpi-spark">{spark}</div>}
    </div>
  );
}
